/**
 * PRICE RANGES CONFIGURATION
 *
 * This file contains the complete configuration for all price filter ranges available on the platform.
 * It serves as the central source of truth for price-based therapist filtering and landing pages.
 *
 * WHAT THIS FILE DOES:
 * - Defines price ranges (up to 40 EUR, up to 50 EUR, 40-50 EUR, etc.)
 * - Maps URL slugs to comprehensive price range data including labels, descriptions, and FAQ items
 * - Provides min/max price values for therapist filtering
 * - Contains SEO metadata (titles, descriptions) for price range landing pages
 * - Includes detailed FAQ sections with Serbian-language Q&A for price concerns
 *
 * KEY FEATURES:
 * - All content is in Serbian language for local market
 * - Each price range includes practical, actionable FAQ answers
 * - Price values are used for actual filtering in the search API
 * - Consistent structure allows for easy maintenance and expansion
 *
 * USAGE:
 * - Used by price range landing pages (/terapija/cena-[range])
 * - Powers therapist search and filtering by price
 * - Generates dynamic SEO content for better search visibility
 * - Provides FAQ content for user education and engagement
 */

export type PriceRangeSlug =
  | "do-40-eura"
  | "do-45-eura"
  | "do-50-eura"
  | "od-40-do-50-eura"
  | "od-50-do-60-eura"
  | "preko-60-eura";

export type FAQItem = {
  question: string;
  answer: string;
};

export type PriceRangeData = {
  label: string;
  minPrice?: number; // in EUR, undefined means no minimum
  maxPrice?: number; // in EUR, undefined means no maximum
  description: string;
  seoTitle: string;
  seoDescription: string;
  faqItems?: FAQItem[];
};

// Mapping between URL slugs and price range data
export const PRICE_RANGES: Record<PriceRangeSlug, PriceRangeData> = {
  "do-40-eura": {
    label: "Terapeuti do 40 EUR",
    maxPrice: 40,
    description:
      "Pronađi kvalitetne psihologe i psihoterapeute čija cena sesije ide do 40 EUR. Pristupačna online terapija bez kompromisa na kvalitetu.",
    seoTitle: "Terapeuti do 40 EUR | Pristupačna Online Terapija",
    seoDescription:
      "Online psihoterapija do 40 EUR po sesiji. Licencirani terapeuti i psiholozi sa provređenim iskustvom nude pristupačnu podršku za mentalno zdravlje.",
    faqItems: [
      {
        question: "Da li su terapeuti do 40 EUR kvalitetni?",
        answer:
          "Da! Cena ne određuje kvalitet. Mnogi iskusni terapeuti nude pristupačnije cene kako bi bili dostupni širem krugu ljudi. Svi terapeuti na platformi su licencirani i provereni.",
      },
      {
        question: "Šta je uključeno u cenu sesije?",
        answer:
          "Cena uključuje punu sesiju od 45 minuta, video poziv visoke kvalitete, i podršku terapeuta između sesija putem poruka (kod nekih terapeuta).",
      },
      {
        question: "Mogu li da dobijem povraćaj novca ako mi se ne svidi?",
        answer:
          "Ako nisi zadovoljan prvom sesijom, kontaktiraj našu podršku i pomoći ćemo ti da pronađeš drugog terapeuta ili razmotriti povraćaj sredstava.",
      },
      {
        question: "Koliko sesija mi treba?",
        answer:
          "To zavisi od tvojih potreba. Neki problemi se mogu rešiti za 4-6 sesija, dok druge stvari zahtevaju duži rad. Terapeut će ti pomoći da napravite plan.",
      },
    ],
  },

  "do-45-eura": {
    label: "Terapeuti do 45 EUR",
    maxPrice: 45,
    description:
      "Pronađi kvalitetne psihologe i psihoterapeute čija cena sesije ide do 45 EUR. Pristupačna online terapija bez kompromisa na kvalitetu.",
    seoTitle: "Terapeuti do 45 EUR | Pristupačna Online Terapija",
    seoDescription:
      "Online psihoterapija do 45 EUR po sesiji. Licencirani terapeuti i psiholozi sa provređenim iskustvom nude pristupačnu podršku za mentalno zdravlje.",
    faqItems: [
      {
        question: "Da li su terapeuti do 45 EUR kvalitetni?",
        answer:
          "Da! Cena ne određuje kvalitet. Mnogi iskusni terapeuti nude pristupačnije cene kako bi bili dostupni širem krugu ljudi. Svi terapeuti na platformi su licencirani i provereni.",
      },
      {
        question: "Šta je uključeno u cenu sesije?",
        answer:
          "Cena uključuje punu sesiju od 45 minuta, video poziv visoke kvalitete, i podršku terapeuta između sesija putem poruka (kod nekih terapeuta).",
      },
      {
        question: "Mogu li da dobijem povraćaj novca ako mi se ne svidi?",
        answer:
          "Ako nisi zadovoljan prvom sesijom, kontaktiraj našu podršku i pomoći ćemo ti da pronađeš drugog terapeuta ili razmotriti povraćaj sredstava.",
      },
      {
        question: "Koliko sesija mi treba?",
        answer:
          "To zavisi od tvojih potreba. Neki problemi se mogu rešiti za 4-6 sesija, dok druge stvari zahtevaju duži rad. Terapeut će ti pomoći da napravite plan.",
      },
    ],
  },

  "do-50-eura": {
    label: "Terapeuti do 50 EUR",
    maxPrice: 50,
    description:
      "Pronađi psihologe i psihoterapeute sa sesijom do 50 EUR. Širok izbor terapeuta različitih specijalnosti po pristupačnim cenama.",
    seoTitle: "Terapeuti do 50 EUR | Online Psiholog i Psihoterapija",
    seoDescription:
      "Psihoterapija online do 50 EUR po sesiji. Izaberi između brojnih terapeuta sa različitim specijalnostima — KBT, psihoanaliza, EMDR i više.",
    faqItems: [
      {
        question: "Zašto su cene različite među terapeutima?",
        answer:
          "Cena zavisi od iskustva terapeuta, dodatnih specijalizacija, tražnje, i toga koliko terapeut želi da bude dostupan klijentima. Svi nude kvalitetnu uslugu.",
      },
      {
        question: "Mogu li da biram terapeuta u ovom cenovnom rangu?",
        answer:
          "Apsolutno! Imaš pristup svim terapeutima u ovom rangu i možeš birati prema specijalnosti, pristupu, raspoloživosti i osećaju koji ti profil pruža.",
      },
      {
        question: "Da li postoje paketi ili popusti?",
        answer:
          "Neki terapeuti nude pakete sa 5 ili 10 sesija po povoljnijoj ceni. Proveri u profilu terapeuta ili pitaj direktno.",
      },
      {
        question: "Mogu li da promenim terapeuta bez dodatnog plaćanja?",
        answer:
          "Da! Možeš slobodno zakazati sa drugim terapeutom. Svaka nova sesija se plaća odvojeno, tako da imaš potpunu fleksibilnost.",
      },
      {
        question: "Šta ako mi trenutno novac predstavlja problem?",
        answer:
          "Razumemo te. Pogledaj terapeute u nižem cenovnom rangu ili nam se javi — možda možemo da ti pomognemo da pronađeš rešenje.",
      },
    ],
  },

  "od-40-do-50-eura": {
    label: "Terapeuti od 40 do 50 EUR",
    minPrice: 40,
    maxPrice: 50,
    description:
      "Iskusni psihoterapeuti i psiholozi sa cenom sesije između 40 i 50 EUR. Pronađi terapeuta koji odgovara tvojim potrebama i budžetu.",
    seoTitle: "Terapeuti od 40 do 50 EUR | Kvalitetna Online Terapija",
    seoDescription:
      "Online psihoterapija sa licenciranim terapeutima u cenovnom rangu 40-50 EUR po sesiji. KBT, sistemska terapija, EMDR i drugi pristupi dostupni.",
    faqItems: [
      {
        question: "Šta dobijam za ovu cenu?",
        answer:
          "Sesiju sa licenciranim terapeutom, siguran prostor za razgovor, stručnu podršku, jasne vežbe između sesija, i često mogućnost kontakta između sesija.",
      },
      {
        question: "Kako znam koji terapeut je pravi za mene?",
        answer:
          "Pročitaj profile, obrati pažnju na njihove specijalnosti i stil rada. Većina terapeuta nudi prvu sesiju kao upoznavanje — ako ne klikne, možeš probati drugog.",
      },
      {
        question: "Da li mogu da dobijem račun za terapiju?",
        answer:
          "Da! Svaka sesija dolazi sa računom koji možeš koristiti za zdravstveno osiguranje (ako tvoje osiguranje pokriva psihoterapiju) ili za lične evidencije.",
      },
      {
        question: "Koliko često se viđam sa terapeutom?",
        answer:
          "Najčešće jednom nedeljno, ali možete dogovoriti i češće sesije u akutnim fazama ili ređe kasnije, zavisno od potreba.",
      },
      {
        question: "Mogu li da otkazujem i zakažem termine fleksibilno?",
        answer:
          "Da, uz poštovanje pravila otkazivanja (obično 24 sata ranije). Terapeuti obično imaju fleksibilne termine, uključujući večernje i vikende.",
      },
    ],
  },

  "od-50-do-60-eura": {
    label: "Terapeuti od 50 do 60 EUR",
    minPrice: 50,
    maxPrice: 60,
    description:
      "Izaberi psihoterapeute sa dodatnim specijalizacijama i bogatim iskustvom. Cena sesije između 50 i 60 EUR za dubinski rad i specijalizovane pristupe.",
    seoTitle: "Terapeuti od 50 do 60 EUR | Specijalizovana Psihoterapija",
    seoDescription:
      "Specijalizovani psihoterapeuti za kompleksne probleme i dubinski rad. Online sesije 50-60 EUR sa iskusnim licenciranim stručnjacima.",
    faqItems: [
      {
        question: "Zašto su ovi terapeuti u višem cenovnom rangu?",
        answer:
          "Često imaju više godina iskustva, dodatne specijalizacije (EMDR, psihoanaliza, trauma-fokusirana terapija) ili rade sa kompleksnijim problemima koji zahtevaju naprednije pristupe.",
      },
      {
        question: "Jesu li bolji od terapeuta sa nižim cenama?",
        answer:
          "Ne nužno 'bolji', već često specijalizovaniji. Svaki terapeut ima svoje jake strane. Biraj prema svojoj specifičnoj potrebi, ne samo ceni.",
      },
      {
        question: "Šta ako ne mogu da priuštim sesije ovako često?",
        answer:
          "Razgovaraj sa terapeutom o učestalosti sesija. Mnogi klijenti prelaze na sesije svakih 2 nedelje nakon početne faze ili kombinuju pristupe.",
      },
      {
        question: "Da li mogu da dobijem superviziju ili dodatnu podršku?",
        answer:
          "Neki terapeuti u ovom rangu nude dodatne usluge kao što su kratki čekovi između sesija, mailove sa resursima, ili fleksibilnije pakete podrške.",
      },
      {
        question: "Kako izgleda proces terapije sa iskusnijim terapeutom?",
        answer:
          "Zavisi od pristupa, ali često uključuje detaljniju procenu na početku, strukturiran plan rada, i kombinaciju različitih tehnika prilagođenih tvojim potrebama.",
      },
    ],
  },

  "preko-60-eura": {
    label: "Terapeuti preko 60 EUR",
    minPrice: 60,
    description:
      "Najiskusniji psihoterapeuti sa specijalizovanim pristupima i bogatom praksom. Premium psihoterapija za kompleksne slučajeve i dubinski rad.",
    seoTitle: "Terapeuti preko 60 EUR | Premium Online Psihoterapija",
    seoDescription:
      "Premium psihoterapija sa najiskusnijim licenciranim terapeutima. Specijalizovani pristupi za trauma, kompleksne poremećaje, i dubinski rad preko 60 EUR po sesiji.",
    faqItems: [
      {
        question: "Šta opravdava višu cenu ovih terapeuta?",
        answer:
          "Obično imaju 10+ godina iskustva, višestruke međunarodne certifikate, specijalizacije za kompleksne poremećaje, ili uske fokuse (npr. traume iz detinjstva, PTSP, dissocijacija).",
      },
      {
        question: "Da li će mi ovaj terapeut pomoći brže nego drugi?",
        answer:
          "Ne nužno brže, ali mogu imati specifične alate za kompleksnije probleme. Ako imaš vrlo specifičan ili težak problem, njihovo iskustvo može biti ključno.",
      },
      {
        question: "Mogu li da dobijem probnu sesiju pre nego se obavežem?",
        answer:
          "Da! Prva sesija je uvek prilika da se upoznate i vidiš da li ti pristup odgovara. Nema obaveze da nastaviš ako ne osećaš klik.",
      },
      {
        question: "Šta ako mi finansijski ne odgovara dugoročno?",
        answer:
          "Razgovaraj otvoreno sa terapeutom. Mogu ti preporučiti kolegu sa nižom cenom nakon inicijalnog rada ili pomoći da napravite održiv plan sesija.",
      },
      {
        question: "Da li ovi terapeuti nude nešto što drugi ne nude?",
        answer:
          "Često nude dublje razumevanje kompleksnih dinamika, kombinacije različitih pristupa, rad sa specifičnim populacijama, ili superviziju i konsultacije za teže slučajeve.",
      },
    ],
  },
};

export const getPriceRangeBySlug = (slug: string): PriceRangeData | null => {
  return PRICE_RANGES[slug as PriceRangeSlug] || null;
};

export const getAllPriceRangeSlugs = (): PriceRangeSlug[] => {
  return Object.keys(PRICE_RANGES) as PriceRangeSlug[];
};

export const getFAQItemsForPriceRange = (slug: string): FAQItem[] => {
  const priceRangeData = getPriceRangeBySlug(slug);
  return priceRangeData?.faqItems || DEFAULT_FAQ_ITEMS;
};

// Default FAQ items for price ranges that don't have specific ones
const DEFAULT_FAQ_ITEMS: FAQItem[] = [
  {
    question: "Kako da izaberem pravog terapeuta za sebe?",
    answer:
      "Možeš da pregledaš profile terapeuta, njihove specijalnosti i stil rada. Ako nisi siguran/na, počni s terapeutom čiji ti se opis najviše sviđa. Posle svake sesije, slobodno možeš da probaš i druge terapeute dok ne nađeš osobu koja ti najviše odgovara.",
  },
  {
    question: "Šta ako mi se ne svidi prvi terapeut?",
    answer:
      "Nema frke! Možeš da promeniš terapeuta kad god poželiš. Cilj nam je da nađeš pravog terapeuta za sebe, i zato smo ti omogućili da isprobaš različite terapeute dok se ne osetiš skroz prijatno.",
  },
  {
    question: "Da li je online terapija isto dobra kao uživo?",
    answer:
      "Istraživanja pokazuju da jeste! Možeš da budeš u svom prostoru gde se osećaš opušteno, što često pomaže da se lakše otvoriš i pričaš o svemu.",
  },
  {
    question: "Kako funkcioniše plaćanje?",
    answer:
      "Platiš svaku sesiju posebno pre zakazivanja. Prihvatamo kartice i PayPal. Dobijaš račun za svaku sesiju koji možeš koristiti za osiguranje ako je primenljivo.",
  },
];
